import { ExtractComponentProp } from '@approvalmax/types';
import { Dropzone, Label, openSplitViewState, useExtensions } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { useDispatch, useSelector } from 'modules/react-redux';
import { memo, useCallback, useMemo } from 'react';
import bemFactory from 'react-bem-factory';
import { contentSplitViewState } from 'shared/states';

import { removeAttachment, uploadAttachments } from '../../actions';
import { AttachmentKind } from '../../data/AttachmentKind';
import { useShowAttachment } from '../../hooks/useShowAttachment';
import { getContext, isLoading } from '../../selectors/pageSelectors';
import { RequestEditMode } from '../../selectors/requestSelectors';
import { useOcr } from './AttachmentsSection.hooks';
import { messages } from './AttachmentsSection.messages';
import { AttachmentsSectionProps } from './AttachmentsSection.types';

const qa = bemFactory.qa('reqf-file-drop-area');

export const AttachmentsSection = memo<AttachmentsSectionProps>((props) => {
    const { request, readOnly, required, maxFilesInList, editMode, extensions = [] } = props;

    const dispatch = useDispatch();
    const context = useSelector(getContext);
    const loading = useSelector(isLoading);

    const contentSplitView = useAtomValue(contentSplitViewState);
    const openSplitView = useAtomValue(openSplitViewState);

    const accept = useExtensions(extensions);

    const {
        uploadOcrAttachments,
        ocrDropzoneSettings,
        ocrButtonProps,
        resetOcrDropzoneSettings,
        isLoadingOcr,
        ocrAttachmentId,
    } = useOcr({ request });

    /**
     * Prepares dropzone value based on the request attachments.
     * If the attachment is being processed by OCR, it shows the progress.
     */
    const dropzoneValue = useMemo(
        () =>
            request.attachments.map((attachment) => ({
                id: attachment.id,
                name: attachment.name,
                label: isLoadingOcr && ocrAttachmentId === attachment.id ? messages.capturing : undefined,
                progress: isLoadingOcr && ocrAttachmentId === attachment.id,
                active:
                    openSplitView &&
                    'attachmentId' in contentSplitView &&
                    contentSplitView.attachmentId === attachment.id,
                readOnly:
                    !attachment.isNew && (editMode === RequestEditMode.Editor || editMode === RequestEditMode.Approver),
            })),
        [request.attachments, openSplitView, contentSplitView, isLoadingOcr, ocrAttachmentId, editMode]
    );

    /**
     * Uploads attachments to the request.
     * If the upload is from the additional button, it uploads the attachment for OCR.
     */
    const onUploadAttachments = useCallback<ExtractComponentProp<typeof Dropzone, 'onUpload'>>(
        (file, isUploadFromAdditionalButton) => {
            if (isUploadFromAdditionalButton) {
                return uploadOcrAttachments(file);
            }

            const response = dispatch(
                uploadAttachments({ files: [file?.source], attachmentKind: AttachmentKind.General })
            );

            return !response ? Promise.reject() : response;
        },
        [dispatch, uploadOcrAttachments]
    );

    /**
     * Removes the attachment from the request.
     */
    const onRemoveAttachment = useCallback<ExtractComponentProp<typeof Dropzone, 'onRemove'>>(
        ({ id }) => dispatch(removeAttachment(id)),
        [dispatch]
    );

    /**
     * Shows the attachment in the split view.
     * If clicked on the attachment with pressed Ctrl, Alt or Cmd, it opens the attachment in a new tab.
     */
    const { showAttachment } = useShowAttachment();
    const onShowAttachment = useCallback<ExtractComponentProp<typeof Dropzone, 'onShow'>>(
        (file, isOnNewTab) => {
            const attachment = request.attachments.find(({ id }) => id === file.id);

            if (attachment) {
                showAttachment(attachment, isOnNewTab);
            }
        },
        [showAttachment, request.attachments]
    );

    return (
        <section>
            <Label
                font='headline'
                size='xxsmall'
                fontWeight='medium'
                color='midnight80'
                spacing='0 0 16'
                required={required}
            >
                {messages.label}
            </Label>

            <Dropzone
                value={dropzoneValue}
                multiple={ocrDropzoneSettings?.multiple ?? true}
                maxSize={context.attachmentMaxSize}
                maxFilesInList={maxFilesInList}
                onUpload={onUploadAttachments}
                onFileDialogCancel={resetOcrDropzoneSettings}
                onDropAccepted={resetOcrDropzoneSettings}
                onDropRejected={resetOcrDropzoneSettings}
                onRemove={onRemoveAttachment}
                onShow={onShowAttachment}
                accept={ocrDropzoneSettings?.accept ?? accept}
                hideButton={readOnly}
                hideZone={readOnly}
                progress={loading}
                additionalUploadButtonProps={ocrButtonProps}
                data-qa={qa()}
            />
        </section>
    );
});

AttachmentsSection.displayName = 'AttachmentsSection';
